<template>
  <multiselect
    v-model="selectedValue"
    :options="routeList"
    :multiple="true"
    :searchable="true"
    :show-labels="false"
    :allowEmpty="true"
    :maxHeight="240"
    label="name"
    track-by="key"
    class="min-w-250p w-auto"
    :placeholder="noRoutes ? $t('flexigo.ridership.noRoutesInRouteGroups') : $t('flexigo.otp.allRoutes')"
    @input="handleChange"
  >
    <span slot="noOptions">
      {{ $t('flexigo.otp.noDataAvailable') }}
    </span>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';

export default {
  name: 'OtpRouteFilter',
  components: {
    Multiselect,
  },
  props: {
    selectedRoutes: {
      type: Array,
      default: () => [],
    },
    selectedDirection: {
      type: String,
      default: 'all',
    },
    selectedWorkingType: {
      type: String,
      default: 'All',
    },
    // Route ids to select (see otpDashboardModule). Applied when it changes, or once the route list has
    // loaded if it changed before that.
    presetRouteIds: {
      type: Array,
      default: null,
    },
    // The picked route groups have no routes, so an empty selection means no data, not every route.
    noRoutes: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedValue: null,
      routeList: [],
      hasPendingPreset: false,
    };
  },
  computed: {
    ...mapGetters('otpDashboard', ['availableRoutes', 'availableRoutesRange', 'filters']),
    // Whether the loaded route list is the current date range's. The list outlives the dashboard, so after
    // a date change it is the previous range's until the new one arrives — too early to apply a preset to.
    routesAreCurrent() {
      const range = this.availableRoutesRange;
      const { startDay, endDay } = this.filters?.dateRange || {};
      return !!range && range.startDay === startDay && range.endDay === endDay;
    },
    filteredRoutesByDirection() {
      const { availableRoutes, selectedDirection, selectedWorkingType } = this;
      if (!availableRoutes || !Array.isArray(availableRoutes)) return [];

      let filteredRoutes = availableRoutes;

      // Filter by direction
      if (selectedDirection !== 'all') {
        const selectedDirLower = selectedDirection.toLowerCase();
        filteredRoutes = filteredRoutes.filter(
          (route) => route.direction && route.direction.toLowerCase() === selectedDirLower,
        );
      }

      // Filter by workingType
      if (selectedWorkingType !== 'All') {
        const targetType = selectedWorkingType.toUpperCase(); // 'Dynamic' -> 'DYNAMIC', 'Fixed' -> 'FIXED'
        filteredRoutes = filteredRoutes.filter((route) => route.workingType === targetType);
      }

      return filteredRoutes;
    },
  },
  watch: {
    selectedRoutes(newVal) {
      this.selectedValue = newVal;
    },
    filteredRoutesByDirection: {
      handler(routes) {
        this.routeList = this.groupRoutesByName(routes);
        if (this.hasPendingPreset) this.applyPreset();
      },
      immediate: true,
    },
    routesAreCurrent(current) {
      if (current && this.hasPendingPreset) this.applyPreset();
    },
    // Immediate: the dashboard is rebuilt on every header filter change, while the route selection lives
    // in the store and outlasts it. The preset it's created with, null included, has to replace that.
    presetRouteIds: {
      immediate: true,
      handler() {
        this.hasPendingPreset = true;
        this.applyPreset();
      },
    },
  },
  methods: {
    formatRouteName(route) {
      if (!route || !route.name) return '';

      let formattedName = route.name;

      // Add direction if routeType is ROUND_TRIP
      if (route.routeType === 'ROUND_TRIP' && route.direction) {
        formattedName = `${formattedName} - ${route.direction}`;
      }

      return formattedName;
    },
    // Routes are grouped by routeName because USCS routes have different routeGroups with the same name.
    groupRoutesByName(routes = []) {
      if (!Array.isArray(routes)) return [];
      const result = [];
      const nameGroups = new Map();

      routes.forEach((route) => {
        const nameKey = (route?.name || '').toLowerCase().trim();

        const formattedRoute = {
          ...route,
          name: this.formatRouteName(route),
          // The multiselect tracks options by this. A selection's `id` can't be used: narrowing a group to
          // some of its routes, or expanding it to all of them (handleChange), changes it.
          key: nameKey ? `name:${nameKey}` : `id:${route.id}`,
        };

        if (!nameKey) {
          result.push(formattedRoute);
          return;
        }

        const existing = nameGroups.get(nameKey);
        if (existing) {
          if (!existing.routeIds) {
            existing.routeIds = existing.id ? [existing.id] : [];
          }
          if (formattedRoute.id) {
            existing.routeIds.push(formattedRoute.id);
          }

          if (formattedRoute.driverIds && Array.isArray(formattedRoute.driverIds)) {
            if (!existing.driverIds) existing.driverIds = [];
            formattedRoute.driverIds.forEach((driverId) => {
              if (!existing.driverIds.includes(driverId)) {
                existing.driverIds.push(driverId);
              }
            });
          }
        } else {
          const nameRoute = {
            ...formattedRoute,
            routeIds: formattedRoute.id ? [formattedRoute.id] : [],
            driverIds: formattedRoute.driverIds ? [...formattedRoute.driverIds] : [],
            isRingGroup: true,
          };
          nameGroups.set(nameKey, nameRoute);
          result.push(nameRoute);
        }
      });

      return result;
    },
    // Selects every name group holding a preset route, narrowed to the preset routes only: a name group
    // can also hold same-named routes of route groups that weren't picked.
    applyPreset() {
      if (!this.presetRouteIds) {
        this.hasPendingPreset = false;
        // Nothing to clear (neither in the store nor shown here): don't commit an empty selection.
        if (!this.selectedRoutes?.length && !this.selectedValue?.length) return;
        this.selectedValue = [];
        this.handleChange();
        return;
      }

      if (!this.routesAreCurrent) return;
      const presetIds = new Set(this.presetRouteIds);
      // Matched against every route, not routeList: that one is narrowed by the direction and working
      // type, which may still be the previous view's, and would drop preset routes outside them.
      this.selectedValue = this.groupRoutesByName(this.availableRoutes)
        .map((group) => {
          const groupIds = group.routeIds?.length ? group.routeIds : [group.id];
          const matchedIds = groupIds.filter((id) => presetIds.has(id));
          if (!matchedIds.length) return null;
          // The group's own workingType is its first route's, which the preset may have dropped. Use the
          // matched routes' type, or none when they differ, so the working-type check looks at each route.
          const types = new Set(
            matchedIds.map((id) => this.availableRoutes.find((route) => route.id === id)?.workingType),
          );
          const workingType = types.size === 1 ? [...types][0] : undefined;
          return { ...group, id: matchedIds[0], routeIds: matchedIds, workingType };
        })
        .filter(Boolean);
      this.hasPendingPreset = false;
      this.handleChange();
      // Tells the module it may fetch: until now its requests would have used the previous selection.
      this.$emit('preset-applied');
    },
    // Called by the module when the route list failed to load. The preset is used up here too: a list
    // loaded later (after an OTP date change) must not overwrite what the user picked meanwhile.
    applyPresetFallback() {
      if (!this.hasPendingPreset || !this.presetRouteIds) return;
      this.selectedValue = this.presetRouteIds.map((id) => ({ id, key: `id:${id}`, name: `#${id}` }));
      this.hasPendingPreset = false;
      this.handleChange();
      this.$emit('preset-applied');
    },
    handleChange() {
      // Expand ring groups to include all route IDs
      // Handle null/undefined selectedValue
      const expandedSelection = (this.selectedValue || []).map((item) => {
        if (item.isRingGroup && item.routeIds && item.routeIds.length > 1) {
          // Return object with all route IDs from the ring
          return {
            ...item,
            id: item.routeIds, // Use array of IDs for ring groups
          };
        }
        return item;
      });
      this.$emit('change', expandedSelection);
    },
  },
};
</script>
